/**
 * Les lectures et le geste qui disent, dans la file, qu'un virement a été lu
 * deux fois — et ce que sa communication structurée désigne vraiment.
 *
 * La règle vit dans `jumeaux.ts` et `queueReason.ts` ; ce fichier ne fait que
 * lire en base ce dont elles ont besoin, et appeler le geste de réunion.
 * Voir la migration `20260927144800_le_coda_reconnait_l_export_csv.sql`.
 */

import { db } from './db';
import { jumeauDe, type LigneBancaire } from './jumeaux';
import type { FactureDeLaCommunication, JumeauEnFile } from './queueReason';

/** Une ligne de la file ou d'un compte, telle qu'on lui cherche un jumeau. */
export interface LigneACompleter {
  id: string;
  valueDate: string;
  amountCents: number;
}

interface CandidatLu extends LigneBancaire {
  codaRef: string | null;
  placeCents: number;
  placeSur: string[];
}

const PAR_LOT = 100;
/* PostgREST plafonne une réponse à mille lignes : on lit par pages, jamais
   une seule fois — un plafond silencieux ferait manquer des jumeaux. */
const PAGE = 1000;

function decouper<T>(items: T[], taille: number): T[][] {
  const lots: T[][] = [];
  for (let i = 0; i < items.length; i += taille) lots.push(items.slice(i, i + taille));
  return lots;
}

function cibleEnClair(a: { target: string; invoices?: { invoice_number: string } | null; credit_note_number?: string | null }): string {
  if (a.target === 'invoice') return a.invoices?.invoice_number ?? 'une facture';
  if (a.target === 'credit_note') return a.credit_note_number ?? 'une note de crédit';
  if (a.target === 'provision') return 'la provision';
  return 'hors périmètre';
}

type LigneLue = {
  id: string; source: string; value_date: string; amount_cents: number;
  counterparty_iban: string | null; structured_digits: string | null; communication_raw: string | null;
  allocated_cents: number; coda_ref: string | null; created_at: string | null;
  payment_allocations?: { target: string; invoices: { invoice_number: string } | null }[] | null;
  refund_allocations?: { target: string; credit_note_number: string | null }[] | null;
};

/**
 * Pour chaque ligne, son jumeau dans l'autre source (relevé CODA ↔ export
 * CSV), s'il en a un.
 *
 * On lit en base toutes les lignes bancaires des mêmes dates et des mêmes
 * montants, puis `jumeauDe` — la règle de l'import — tranche. Une ligne CSV
 * déjà reliée à un relevé n'est le jumeau de personne : son relevé est déjà
 * réuni avec elle. Une paire déclarée distincte non plus.
 */
export async function lireLesJumeaux(
  sens: 'entree' | 'sortie',
  lignes: LigneACompleter[],
): Promise<Map<string, JumeauEnFile>> {
  const resultat = new Map<string, JumeauEnFile>();
  if (lignes.length === 0) return resultat;
  const table = sens === 'entree' ? 'payments' : 'refunds';
  const placements = sens === 'entree'
    ? 'payment_allocations(target, invoices(invoice_number))'
    : 'refund_allocations(target, credit_note_number)';

  const lus = new Map<string, CandidatLu>();
  for (const lot of decouper(lignes, PAR_LOT)) {
    const dates = [...new Set(lot.map((l) => l.valueDate))];
    const montants = [...new Set(lot.map((l) => l.amountCents))];
    for (let depuis = 0; ; depuis += PAGE) {
      const { data, error } = await db
        .from(table)
        .select(`id, source, value_date, amount_cents, counterparty_iban, structured_digits,
                 communication_raw, allocated_cents, coda_ref, created_at, ${placements}`)
        .in('source', ['bank', 'bank_csv'])
        .in('value_date', dates)
        .in('amount_cents', montants)
        .order('id', { ascending: true })
        .range(depuis, depuis + PAGE - 1);
      if (error) throw error;
      const page = (data ?? []) as unknown as LigneLue[];
      for (const l of page) {
        const allocations = (l.payment_allocations ?? l.refund_allocations ?? []) as Parameters<typeof cibleEnClair>[0][];
        lus.set(l.id, {
          id: l.id,
          source: l.source,
          valueDate: l.value_date,
          amountCents: Number(l.amount_cents),
          counterpartyIban: l.counterparty_iban,
          structuredDigits: l.structured_digits,
          communicationRaw: l.communication_raw,
          createdAt: l.created_at,
          codaRef: l.coda_ref,
          placeCents: Number(l.allocated_cents ?? 0),
          placeSur: [...new Set(allocations.map(cibleEnClair))],
        });
      }
      if (page.length < PAGE) break;
    }
  }

  /* Les paires qu'un humain a déclarées distinctes, parmi celles qu'on vient de lire. */
  const distinctes = new Set<string>();
  const ids = [...lus.keys()];
  for (const lot of decouper(ids, PAR_LOT)) {
    const liste = lot.join(',');
    const { data, error } = await db
      .from('mouvements_bancaires_distincts')
      .select('coda_id, csv_id')
      .eq('sens', sens)
      .or(`coda_id.in.(${liste}),csv_id.in.(${liste})`);
    if (error) throw error;
    for (const d of (data ?? []) as { coda_id: string; csv_id: string }[]) distinctes.add(`${d.coda_id}|${d.csv_id}`);
  }

  const libres = [...lus.values()].filter((c) => !c.codaRef);
  for (const demandee of lignes) {
    const elle = lus.get(demandee.id);
    if (!elle || elle.codaRef) continue;
    const j = jumeauDe(elle, libres, distinctes);
    if (!j) continue;
    resultat.set(demandee.id, {
      id: j.autre.id,
      source: j.autre.source,
      valueDate: j.autre.valueDate,
      communications: j.communications,
      placeCents: j.autre.placeCents,
      placeSur: j.autre.placeSur,
    });
  }
  return resultat;
}

/**
 * La facture qui porte chacune de ces communications structurées.
 *
 * Une communication absente de la réponse a été cherchée et n'appartient à
 * aucune de nos factures : c'est le seul cas où la file peut parler d'un
 * autre système.
 */
export async function lireLesFacturesStructurees(
  chiffres: string[],
): Promise<Map<string, FactureDeLaCommunication>> {
  const resultat = new Map<string, FactureDeLaCommunication>();
  const uniques = [...new Set(chiffres.filter(Boolean))];
  for (const lot of decouper(uniques, 50)) {
    const { data, error } = await db
      .from('v_invoice_balances')
      .select('invoice_number, amount_cents, allocated_cents, credit_notes_cents, status, structured_communication')
      .in('structured_communication', lot);
    if (error) throw error;
    for (const f of (data ?? []) as unknown as {
      invoice_number: string; amount_cents: number; allocated_cents: number;
      credit_notes_cents: number | null; status: string; structured_communication: string;
    }[]) {
      resultat.set(f.structured_communication, {
        numero: f.invoice_number,
        resteCents: Math.max(0, Number(f.amount_cents) - Number(f.credit_notes_cents ?? 0) - Number(f.allocated_cents)),
        annulee: f.status === 'cancelled',
      });
    }
  }
  return resultat;
}

/**
 * Réunit deux lignes qui décrivent le même mouvement : `garder` reste,
 * `retirer` part à l'archive. La base refuse de retirer une ligne placée vers
 * une facture, une provision ou une note — le geste le dit, l'écran l'affiche.
 */
export async function reunirLesDeuxLignes(
  sens: 'entree' | 'sortie',
  garder: string,
  retirer: string,
  motif: string,
): Promise<void> {
  const { error } = await db.rpc('reunir_mouvements_bancaires', {
    p_sens: sens,
    p_garder: garder,
    p_retirer: retirer,
    p_motif: motif,
  });
  if (error) throw error;
}

/**
 * Deux lignes de même forme sont deux virements distincts — une répétition
 * légitime. Consigné (qui, quand, pourquoi) : le contrôle et la file ne les
 * signalent plus. N'écrit rien d'autre.
 */
export async function declarerDistincts(
  sens: 'entree' | 'sortie',
  idReleve: string,
  idExport: string,
  motif: string,
): Promise<void> {
  const { error } = await db.rpc('declarer_mouvements_distincts', {
    p_sens: sens,
    p_coda: idReleve,
    p_csv: idExport,
    p_motif: motif,
  });
  if (error) throw error;
}

export interface DoublonsBancaires {
  /** `identiques` et `une_absente` : écrits deux fois, doit être zéro. */
  doublons: number;
  /** `differentes` : à trancher par un humain. */
  probables: number;
}

/** Le contrôle `v_mouvements_bancaires_en_double`, compté. Ne décide rien. */
export async function compterLesDoublonsBancaires(): Promise<DoublonsBancaires> {
  const [doublons, probables] = await Promise.all([
    db.from('v_mouvements_bancaires_en_double').select('coda_id', { count: 'exact', head: true })
      .in('communications', ['identiques', 'une_absente']),
    db.from('v_mouvements_bancaires_en_double').select('coda_id', { count: 'exact', head: true })
      .eq('communications', 'differentes'),
  ]);
  if (doublons.error) throw doublons.error;
  if (probables.error) throw probables.error;
  return { doublons: doublons.count ?? 0, probables: probables.count ?? 0 };
}
